import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Shell from './Shell';
import QuizGame from './QuizGame';
import useAudio from '../useAudio';

// ── Séance prescrite par le tuteur (page personnelle) ───────────────────────
// Accès via /?serie=12,45,… Cette page ne fait que composer la séance : le
// moteur est `tuteur/QuizGame.jsx`, copie fidèle du quiz public dont seuls les
// points incompatibles avec une prescription ont été changés (liste figée, revue
// séquentielle, aucune persistance, route d'enregistrement propre).
//
// Elle NE PARTAGE RIEN avec le parcours public, sauf `useAudio` — un lecteur de
// son sans état partagé, qu'il n'y avait aucune raison de dupliquer.

const TOKEN_KEY = 'motrusse_token';
const SERIE_MAX = 20;

export default function Quiz({ cursive, toggleCursive }) {
  const [phase, setPhase] = useState('loading');
  const [mots, setMots] = useState([]);
  const [faits, setFaits] = useState(0);
  const [justes, setJustes] = useState(0);
  const [passees, setPassees] = useState(0);
  const { soundEnabled, toggleSound, play, playAuto, whenAudioIdle, stopAudio } = useAudio();
  const [token] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });

  const ids = useMemo(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get('serie') || '';
      return raw.split(',').filter((x) => /^\d+$/.test(x)).map(Number).slice(0, SERIE_MAX);
    } catch { return []; }
  }, []);

  useEffect(() => {
    const leave = () => { try { window.location.replace('/'); } catch {} };
    if (!token) { leave(); return; }
    if (!ids.length) { setPhase('vide'); return; }
    fetch('/api/tuteur/quiz/serie', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ids }),
    })
      .then((r) => {
        if (r.status === 401 || r.status === 403) { leave(); return null; }
        if (!r.ok) throw new Error('http');
        return r.json();
      })
      .then((d) => {
        if (!d) return;
        setMots(d.mots || []);
        setPhase((d.mots || []).length ? 'quiz' : 'vide');
      })
      .catch(() => setPhase('erreur'));
  }, [token, ids]);

  // `correct` vaut true / false / null. `null` = carte VUE mais non jugée (chrono
  // écoulé, ou exercice non constructible) : elle fait avancer la série — sans ça
  // une prescription de dix mots ne pouvait plus jamais atteindre dix — mais elle
  // ne compte ni juste ni faux, sinon le bilan mentirait dans les deux sens.
  const onAnswered = useCallback((correct) => {
    setFaits((n) => n + 1);
    if (correct === true) setJustes((n) => n + 1);
    else if (correct === null) setPassees((n) => n + 1);
  }, []);

  // La série s'arrête quand tous les mots prescrits ont été posés une fois.
  const termine = mots.length > 0 && faits >= mots.length;

  if (phase === 'loading') {
    return (
      <Shell actif="/?tuteur" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </Shell>
    );
  }

  if (phase === 'erreur' || phase === 'vide') {
    return (
      <Shell actif="/?tuteur" cursive={cursive} toggleCursive={toggleCursive}>
        <p className="text-slate-400 pt-10">
          {phase === 'vide' ? 'Aucun mot dans cette série.' : 'Impossible de charger la série.'}
        </p>
        <a href="/?tuteur" className="self-start px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors">
          Retour au tuteur
        </a>
      </Shell>
    );
  }

  return (
    <Shell actif="/?tuteur" cursive={cursive} toggleCursive={toggleCursive}>
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl md:text-2xl font-bold">Série prescrite</h1>
        <span className="text-xs text-slate-500 tabular-nums">
          {Math.min(faits + (termine ? 0 : 1), mots.length)} / {mots.length}
          {faits > 0 && ` · ${justes} juste${justes > 1 ? 's' : ''}`}
        </span>
      </div>

      {termine ? (
        <div className="flex flex-col gap-4">
          <p className="text-slate-300">
            Série terminée : {justes} sur {faits - passees}
            {passees > 0 && ` · ${passees} passée${passees > 1 ? 's' : ''} au chrono`}.
          </p>
          <a href="/?tuteur" className="self-start px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors">
            Retour au tuteur
          </a>
        </div>
      ) : (
        <QuizGame
          words={mots}
          activeFilter={6000}
          lastIncorrectIds={[]}
          setLastIncorrectIds={() => {}}
          isLoading={false}
          error={null}
          userId={null}
          seedWordId={null}
          themes={null}
          chapterSlug={null}
          explore={false}
          filterTheme={null}
          landing
          onStatsUpdate={() => {}}
          sessionKey={0}
          soundEnabled={soundEnabled}
          toggleSound={toggleSound}
          play={play}
          playAuto={playAuto}
          whenAudioIdle={whenAudioIdle}
          stopAudio={stopAudio}
          onAnswered={onAnswered}
          onRescued={() => {}}
          frozen={false}
          seriesDone={faits}
          seriesTotal={mots.length}
        />
      )}
    </Shell>
  );
}
